import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { useT } from '../i18n';
import { isCapacitorNativeSync } from '../native/bridge';
import { getConsent, initAnalytics, setConsent, subscribeConsent } from './analytics';
import styles from './CookieConsent.module.css';

// Consent-Banner + Analytics-Bootstrap fuer das Web.
//
// Striktes Opt-in fuer ALLE (P15): Der Banner wird jedem Web-Besucher gezeigt,
// bis er einmalig entschieden hat — Gaesten UND eingeloggten Nutzern. Die
// fruehere Annahme, die AGB-Zustimmung decke das eigene Tracking eingeloggter
// Nutzer ab, war rechtlich keine Einwilligung. Die Entscheidung persistiert im
// 365-Tage-Consent-Cookie, es wird also nur EINMAL gefragt.
//
// In der nativen App rendert die Komponente NICHTS und startet auch kein
// Analytics: Google Ads im App-Wrapper ist ein App-Review-Blocker.
export function CookieConsent() {
  const { t } = useT();
  const native = isCapacitorNativeSync();
  const [consent, setConsentState] = useState(getConsent);

  // Analytics-Bootstrap: setzt die Consent-Mode-Defaults (Pflicht VOR jedem
  // gtag-Treffer — sendet selbst nichts) und aktiviert NUR bei ausdruecklicher
  // Einwilligung ('accepted') Tracking/Ads. Laeuft einmalig beim Mount; in der
  // nativen App gar nicht.
  useEffect(() => {
    if (native) return;
    initAnalytics();
  }, [native]);

  useEffect(() => subscribeConsent(setConsentState), []);

  // Der Banner liegt position:fixed ueber allem und verdeckte sonst dauerhaft
  // den unteren Seitenrand — auf /kontakt das Nachrichtenfeld samt
  // Absenden-Knopf, auf Login/Reset die Fusszeile. Seine gemessene Hoehe steht
  // deshalb als CSS-Variable am <html>; die Seiten-Container reservieren
  // entsprechend Platz. Gemessen statt geschaetzt, weil der Banner je nach
  // Breite und Sprache ein- oder zweizeilig laeuft.
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const setInset = useCallback((px: number) => {
    document.documentElement.style.setProperty('--lorath-consent-inset', `${Math.ceil(px)}px`);
  }, []);

  const visible = !native && consent === null;

  useEffect(() => {
    if (!visible) {
      document.documentElement.style.removeProperty('--lorath-consent-inset');
      return undefined;
    }
    const node = bannerRef.current;
    if (!node) return undefined;
    setInset(node.getBoundingClientRect().height);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setInset(entry.contentRect.height);
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--lorath-consent-inset');
    };
  }, [visible, setInset]);

  // Kein Banner: in der App und sobald eine Entscheidung im Cookie steht.
  if (!visible) return null;

  return (
    <div ref={bannerRef} className={styles.banner} role="dialog" aria-live="polite" aria-label={t('cookie.title')}>
      <div className={styles.inner}>
        <p className={styles.text}>
          {t('cookie.text')}{' '}
          <Link className={styles.link} to="/legal/datenschutz">
            {t('cookie.privacy_link')}
          </Link>
        </p>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setConsent('rejected')}>
            {t('cookie.reject')}
          </Button>
          <Button variant="primary" onClick={() => setConsent('accepted')}>
            {t('cookie.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
}
